import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { besprechungTeilnehmerSortiert, type BesprechungTeilnehmer, type SiteDocDetail, type SiteDocProjectContact } from '@symphenia/shared-types'
import { Feld as Spalte, ZeilenBlock, ZeilenListe, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import { FeldStatusZeichen, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { fehlertext } from '@/lib/fehlertext'
import { besprechungKontakte, besprechungMitarbeiter, teilnehmerAendern, teilnehmerAnlegen, teilnehmerLoeschen, type BesprechungMitarbeiter } from './api'

/** Ein Vorschlag für einen Teilnehmer — aus dem Projekt (mit Firma, RN-9) oder ein Mitarbeiter des Betriebs. */
export interface TeilnehmerVorschlag {
  /** Was in der Liste steht und im Feld gesucht wird. */
  text: string
  name: string
  company: string | null
  function: string | null
  email: string | null
  userId: string | null
  contactId: string | null
}

/** Vorschläge aus Projektkontakten und Mitarbeitern — einmal je Protokoll geladen. */
export function useTeilnehmerVorschlaege(dok: SiteDocDetail, aktiv: boolean, firma: string): TeilnehmerVorschlag[] {
  const [kontakte, setKontakte] = useState<SiteDocProjectContact[]>([])
  const [leute, setLeute] = useState<BesprechungMitarbeiter[]>([])
  useEffect(() => {
    if (!aktiv) return
    besprechungKontakte(dok.id).then(setKontakte).catch(() => setKontakte([]))
    besprechungMitarbeiter().then(setLeute).catch(() => setLeute([]))
  }, [aktiv, dok.id])
  return useMemo(
    () => [
      ...kontakte.map((k) => ({ text: [k.name, k.company].filter(Boolean).join(' — '), name: k.name, company: k.company ?? null, function: k.function, email: k.email, userId: null, contactId: k.id })),
      ...leute.map((u) => ({ text: `${u.name} — ${firma}`, name: u.name, company: firma || null, function: null, email: u.email, userId: u.id, contactId: null })),
    ],
    [kontakte, leute, firma],
  )
}

const ANWESENHEITEN = ['PRESENT', 'EXCUSED', 'ABSENT'] as const

function spalten(t: (k: string) => string, mitAnwesenheit: boolean): SpaltenDef[] {
  return [
    { key: 'name', label: t('siteDocs.meeting.name'), spur: 'minmax(10rem,2fr)', min: 160 },
    { key: 'company', label: t('siteDocs.meeting.company'), spur: 'minmax(8rem,1.5fr)', min: 128 },
    { key: 'function', label: t('siteDocs.meeting.function'), spur: 'minmax(7rem,1fr)', min: 112 },
    ...(mitAnwesenheit ? [{ key: 'attendance', label: t('siteDocs.meeting.attendance'), spur: '9.5rem', min: 152 }] : []),
    { key: 'distribution', label: 'V', spur: '2rem', min: 32 },
    { key: 'email', label: t('siteDocs.meeting.email'), spur: 'minmax(10rem,1.5fr)', min: 160 },
    { key: 'aktion', label: '', spur: '2rem', min: 32 },
  ]
}

const alsDatensatz = (p: BesprechungTeilnehmer): Datensatz => ({
  name: p.name,
  company: p.company ?? null,
  function: p.function ?? null,
  attendance: p.attendance ?? null,
  distribution: p.distribution,
  email: p.email ?? null,
})

function TeilnehmerZeile({ dok, p, mitAnwesenheit, onGeaendert, onFehler }: { dok: SiteDocDetail; p: BesprechungTeilnehmer; mitAnwesenheit: boolean; onGeaendert: (neu: SiteDocDetail) => void; onFehler: (t: string | null) => void }) {
  const { t } = useTranslation()
  const daten = useMemo(() => alsDatensatz(p), [p])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `besprechungTeilnehmer:${p.id}`,
    bezeichnung: p.name,
    daten,
    schreibe: async (patch) => {
      const neu = await teilnehmerAendern(dok.id, p.id!, patch)
      onGeaendert(neu)
      const z = ((neu.content?.teilnehmer ?? []) as BesprechungTeilnehmer[]).find((x) => x.id === p.id)
      return z ? alsDatensatz(z) : daten
    },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: p.name })
  const name = useFeld(stelle, 'name', { gruppe, pflicht: true, label: t('siteDocs.meeting.name') })
  const firma = useFeld(stelle, 'company', { gruppe, label: t('siteDocs.meeting.company') })
  const funktion = useFeld(stelle, 'function', { gruppe, label: t('siteDocs.meeting.function') })
  const anwesenheit = useFeld(stelle, 'attendance', { gruppe, art: 'auswahl', label: t('siteDocs.meeting.attendance') })
  const verteiler = useFeld<boolean>(stelle, 'distribution', { gruppe, art: 'schalter', label: t('siteDocs.meeting.distribution') })
  const email = useFeld(stelle, 'email', { gruppe, label: t('siteDocs.meeting.email'), pruefe: (w) => (!w || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(w)) ? null : t('siteDocs.meeting.emailInvalid')) })
  return (
    <ZeilenBlock gruppe={gruppe} schluessel={p.id}>
      <Spalte spalte="name">
        <input className={zelle} data-start aria-label={t('siteDocs.meeting.name')} {...name.props} />
      </Spalte>
      <Spalte spalte="company">
        <input className={zelle} aria-label={t('siteDocs.meeting.company')} {...firma.props} />
      </Spalte>
      <Spalte spalte="function">
        <input className={zelle} aria-label={t('siteDocs.meeting.function')} {...funktion.props} />
      </Spalte>
      {mitAnwesenheit && (
        <Spalte spalte="attendance">
          <select className={zelle} aria-label={t('siteDocs.meeting.attendance')} {...anwesenheit.props}>
            <option value="">{t('siteDocs.meeting.onlyDistribution')}</option>
            {ANWESENHEITEN.map((a) => (
              <option key={a} value={a}>
                {t(`siteDocs.meeting.attendances.${a}`)}
              </option>
            ))}
          </select>
        </Spalte>
      )}
      <Spalte spalte="distribution">
        <span className="flex items-center justify-center gap-1" title={t('siteDocs.meeting.distribution')}>
          <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" aria-label={t('siteDocs.meeting.distribution')} {...verteiler.schalter} />
          <FeldStatusZeichen status={verteiler} />
        </span>
      </Spalte>
      <Spalte spalte="email">
        <input className={zelle} type="email" aria-label={t('siteDocs.meeting.email')} {...email.props} />
      </Spalte>
      <Spalte spalte="aktion">
        <button
          type="button"
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label={t('siteDocs.meeting.removeParticipant')}
          title={t('siteDocs.meeting.removeParticipant')}
          onClick={() => {
            onFehler(null)
            teilnehmerLoeschen(dok.id, p.id!).then(onGeaendert, (e) => onFehler(fehlertext(e, t)))
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </Spalte>
    </ZeilenBlock>
  )
}

/** Die leere letzte Zeile: Name tippen oder aus den Vorschlägen wählen — beim Verlassen angelegt. */
function NeuerTeilnehmer({ dok, vorschlaege, mitAnwesenheit, onGeaendert, onFehler }: { dok: SiteDocDetail; vorschlaege: TeilnehmerVorschlag[]; mitAnwesenheit: boolean; onGeaendert: (neu: SiteDocDetail) => void; onFehler: (t: string | null) => void }) {
  const { t } = useTranslation()
  const leer = { name: '', company: '', function: '', attendance: mitAnwesenheit ? 'PRESENT' : '', email: '' }
  const [neu, setNeu] = useState(leer)
  const [wahl, setWahl] = useState<TeilnehmerVorschlag | null>(null)
  const liste = `besprechung-vorschlaege-${dok.id}`

  const waehle = (text: string) => {
    const v = vorschlaege.find((x) => x.text === text) ?? null
    setWahl(v)
    setNeu(v ? { ...neu, name: v.name, company: v.company ?? '', function: v.function ?? '', email: v.email ?? '' } : { ...neu, name: text })
  }

  const anlegen = async () => {
    if (!neu.name.trim()) return
    onFehler(null)
    try {
      const r = await teilnehmerAnlegen(dok.id, {
        name: neu.name.trim(),
        company: neu.company.trim() || null,
        function: neu.function.trim() || null,
        attendance: neu.attendance || null,
        email: neu.email.trim() || null,
        userId: wahl?.userId ?? null,
        contactId: wahl?.contactId ?? null,
      })
      setNeu(leer)
      setWahl(null)
      onGeaendert(r)
    } catch (e) {
      onFehler(fehlertext(e, t))
    }
  }

  return (
    <ZeilenBlock neu autoFokus={false} onVerlassen={() => void anlegen()} onVerwerfen={() => setNeu(leer)}>
      <Spalte spalte="name">
        <input className={zelle} list={liste} placeholder={t('siteDocs.meeting.newParticipant')} aria-label={t('siteDocs.meeting.newParticipant')} value={neu.name} onChange={(e) => waehle(e.target.value)} />
        <datalist id={liste}>
          {vorschlaege.map((v) => (
            <option key={`${v.contactId ?? ''}${v.userId ?? ''}${v.text}`} value={v.text} />
          ))}
        </datalist>
      </Spalte>
      <Spalte spalte="company">
        <input className={zelle} aria-label={t('siteDocs.meeting.company')} value={neu.company} onChange={(e) => setNeu({ ...neu, company: e.target.value })} />
      </Spalte>
      <Spalte spalte="function">
        <input className={zelle} aria-label={t('siteDocs.meeting.function')} value={neu.function} onChange={(e) => setNeu({ ...neu, function: e.target.value })} />
      </Spalte>
      {mitAnwesenheit && (
        <Spalte spalte="attendance">
          <select className={zelle} aria-label={t('siteDocs.meeting.attendance')} value={neu.attendance} onChange={(e) => setNeu({ ...neu, attendance: e.target.value })}>
            <option value="">{t('siteDocs.meeting.onlyDistribution')}</option>
            {ANWESENHEITEN.map((a) => (
              <option key={a} value={a}>
                {t(`siteDocs.meeting.attendances.${a}`)}
              </option>
            ))}
          </select>
        </Spalte>
      )}
      <Spalte spalte="distribution" leer>
        <span />
      </Spalte>
      <Spalte spalte="email">
        <input className={zelle} type="email" aria-label={t('siteDocs.meeting.email')} value={neu.email} onChange={(e) => setNeu({ ...neu, email: e.target.value })} />
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}

/**
 * AP-49 5.1 — **Teilnehmer und Verteiler** als Zeilenblock: Anwesenheit, Haken „Verteiler", E-Mail für den
 * Versand (nicht im Inhalt, BS-8). Vorschläge aus den Projekt-Beteiligten mit Firma (RN-9) und den
 * Mitarbeitern des Betriebs; frei eintippen geht immer. In der Berichtigung nur der Verteiler.
 */
export function TeilnehmerBlock({ dok, teilnehmer, mitAnwesenheit, firma, onGeaendert }: { dok: SiteDocDetail; teilnehmer: BesprechungTeilnehmer[]; mitAnwesenheit: boolean; firma: string; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const [fehler, setFehler] = useState<string | null>(null)
  const vorschlaege = useTeilnehmerVorschlaege(dok, true, firma)
  const sortiert = besprechungTeilnehmerSortiert(teilnehmer)
  const anwesend = teilnehmer.filter((p) => p.attendance === 'PRESENT').length
  return (
    <section className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium">{mitAnwesenheit ? t('siteDocs.meeting.participants') : t('siteDocs.meeting.distributionList')}</h3>
        {mitAnwesenheit && <span className="text-xs text-muted-foreground">{t('siteDocs.meeting.presentCount', { anwesend, gesamt: teilnehmer.length })}</span>}
      </div>
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
      <ZeilenListe spalten={spalten(t, mitAnwesenheit)}>
        {sortiert.map((p) => (
          <TeilnehmerZeile key={p.id ?? p.clientId} dok={dok} p={p} mitAnwesenheit={mitAnwesenheit} onGeaendert={onGeaendert} onFehler={setFehler} />
        ))}
        <NeuerTeilnehmer dok={dok} vorschlaege={vorschlaege} mitAnwesenheit={mitAnwesenheit} onGeaendert={onGeaendert} onFehler={setFehler} />
      </ZeilenListe>
      <p className="text-xs text-muted-foreground">{t('siteDocs.meeting.participantsHint')}</p>
    </section>
  )
}
